import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import Heading from '../../components/Heading';
import { api, ApiRoutes } from '../../services/ApiService';
import { AdminTypeService } from '../../services/AdminTypeService';
import { showToast } from '../../services/ToastService';
import { Type } from '../../types/Type';
import { Category } from '../../types/Category';
import { CategoryOption, flattenCategories } from '../../helpers/categoryHelper';
import { getApiErrorMessage } from '../../helpers/apiErrorHelper';
import styles from './TypesOverview.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTrash,
    faImage,
    faSave,
    faXmark,
    faFolderTree,
    faTag,
    faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { openModal, closeModal } from '../../services/ModalService';
import { DeletePictureModal } from './DeletePictureModal';
import { DeleteBranchModal } from './DeleteBranchModal';

interface TypeDraft {
    name: string;
    kKategorie: number | null;
}

const CATEGORY_REQUIRED_MESSAGE = 'Bitte eine Shop-Kategorie auswählen.';

function isCategorySelected(kKategorie: number | null | undefined): boolean {
    return kKategorie != null && kKategorie > 0;
}

const TypesOverview: React.FC = () => {
    const [types, setTypes] = useState<Type[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
    const [drafts, setDrafts] = useState<Record<number, TypeDraft>>({});
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState<number | null>(null);
    const [creating, setCreating] = useState(false);

    const [newName, setNewName] = useState('');
    const [newCategoryId, setNewCategoryId] = useState<number | null>(null);
    const [newPicture, setNewPicture] = useState<File | null>(null);
    const newPictureInputRef = useRef<HTMLInputElement>(null);

    const pictureBaseUrl = useMemo(() => `${api.getBaseUrl()}storage/pictures/`, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const typesResult = await AdminTypeService.getAllTypes();
            setTypes(typesResult);
            setDrafts(
                Object.fromEntries(
                    typesResult.map((type) => [
                        type.id,
                        {
                            name: type.name,
                            kKategorie: type.kKategorie ?? null,
                        },
                    ])
                )
            );
        } catch {
            showToast('Branchen konnten nicht geladen werden', 'error');
            setTypes([]);
            setDrafts({});
        }

        try {
            const categoriesResponse = await api.get(ApiRoutes.CATEGORIES_SHOP);
            const categories: Category[] = categoriesResponse.data?.data?.categories ?? [];
            setCategoryOptions(flattenCategories(categories));
        } catch {
            setCategoryOptions([]);
            showToast('Shop-Kategorien konnten nicht geladen werden', 'warning');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadData();
    }, [loadData]);

    const updateDraft = useCallback((id: number, patch: Partial<TypeDraft>) => {
        setDrafts((prev) => ({
            ...prev,
            [id]: { ...prev[id], ...patch },
        }));
    }, []);

    const handleCreate = async () => {
        if (!newName.trim()) {
            showToast('Bitte einen Namen eingeben', 'error');
            return;
        }

        if (!isCategorySelected(newCategoryId)) {
            showToast(CATEGORY_REQUIRED_MESSAGE, 'error');
            return;
        }

        try {
            setCreating(true);
            const created = await AdminTypeService.createType(newName, newCategoryId, newPicture ?? undefined);
            showToast('Branche angelegt', 'success');
            setNewName('');
            setNewCategoryId(null);
            setNewPicture(null);
            if (newPictureInputRef.current) {
                newPictureInputRef.current.value = '';
            }
            setTypes((prev) => [...prev, created]);
            setDrafts((prev) => ({
                ...prev,
                [created.id]: {
                    name: created.name,
                    kKategorie: created.kKategorie ?? null,
                },
            }));
        } catch (error) {
            showToast(getApiErrorMessage(error, 'Fehler beim Anlegen'), 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleSave = async (id: number) => {
        const draft = drafts[id];
        if (!draft?.name.trim()) {
            showToast('Name darf nicht leer sein', 'error');
            return;
        }

        if (!isCategorySelected(draft.kKategorie)) {
            showToast(CATEGORY_REQUIRED_MESSAGE, 'error');
            return;
        }

        try {
            setSavingId(id);
            const updated = await AdminTypeService.updateType(id, draft.name, draft.kKategorie);
            setTypes((prev) => prev.map((type) => (type.id === id ? updated : type)));
            showToast('Gespeichert', 'success');
        } catch (error) {
            showToast(getApiErrorMessage(error, 'Fehler beim Speichern'), 'error');
        } finally {
            setSavingId(null);
        }
    };

    const handlePictureUpload = async (id: number, file: File) => {
        try {
            setSavingId(id);
            const updated = await AdminTypeService.uploadPicture(id, file);
            setTypes((prev) => prev.map((type) => (type.id === id ? updated : type)));
            showToast('Bild gespeichert', 'success');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bild-Upload fehlgeschlagen';
            showToast(message, 'error');
        } finally {
            setSavingId(null);
        }
    };

    const handlePictureDelete = useCallback(async (id: number) => {
        try {
            setSavingId(id);
            const updated = await AdminTypeService.deletePicture(id);
            setTypes((prev) => prev.map((type) => (type.id === id ? updated : type)));
            showToast('Bild entfernt', 'success');
        } catch {
            showToast('Bild konnte nicht entfernt werden', 'error');
        } finally {
            setSavingId(null);
        }
    }, []);

    const openDeletePictureModal = useCallback(
        (type: Type) => {
            const modalIdRef = { current: '' };
            modalIdRef.current = openModal({
                content: (
                    <DeletePictureModal
                        branchName={type.name}
                        onConfirm={() => {
                            void handlePictureDelete(type.id);
                            closeModal(modalIdRef.current);
                        }}
                        onCancel={() => closeModal(modalIdRef.current)}
                    />
                ),
                size: 'sm',
                onClose: () => closeModal(modalIdRef.current),
            });
        },
        [handlePictureDelete]
    );

    const handleDeleteType = useCallback(async (id: number) => {
        try {
            setSavingId(id);
            await AdminTypeService.deleteType(id);
            setTypes((prev) => prev.filter((type) => type.id !== id));
            setDrafts((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            showToast('Branche gelöscht', 'success');
        } catch {
            showToast('Branche konnte nicht gelöscht werden', 'error');
        } finally {
            setSavingId(null);
        }
    }, []);

    const openDeleteBranchModal = useCallback(
        (type: Type) => {
            const modalIdRef = { current: '' };
            modalIdRef.current = openModal({
                content: (
                    <DeleteBranchModal
                        branchName={type.name}
                        onConfirm={() => {
                            void handleDeleteType(type.id);
                            closeModal(modalIdRef.current);
                        }}
                        onCancel={() => closeModal(modalIdRef.current)}
                    />
                ),
                size: 'sm',
                onClose: () => closeModal(modalIdRef.current),
            });
        },
        [handleDeleteType]
    );

    const categorySelect = (
        value: number | null,
        onChange: (value: number | null) => void,
        options?: { id?: string; showHint?: boolean }
    ) => {
        const missingCategory = !isCategorySelected(value);

        return (
            <>
                <select
                    id={options?.id}
                    className={cx(
                        styles['field-select'],
                        options?.showHint && missingCategory && styles['field-select-error']
                    )}
                    value={value ?? ''}
                    onChange={(e) => {
                        const next = e.target.value ? Number(e.target.value) : null;
                        onChange(next);
                    }}
                >
                    <option value="">Kategorie wählen …</option>
                    {categoryOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {options?.showHint && (
                    <p
                        className={cx(
                            styles['field-hint'],
                            !missingCategory && styles['field-hint-empty']
                        )}
                    >
                        {missingCategory ? CATEGORY_REQUIRED_MESSAGE : '\u00A0'}
                    </p>
                )}
            </>
        );
    };

    if (loading) {
        return (
            <div className={styles['loading-container']}>Branchen werden geladen …</div>
        );
    }

    return (
        <div className={styles['overview-container']}>
            <div className={styles['overview-header']}>
                <Heading level={1} classes={styles['overview-title']}>
                    Branchen
                </Heading>
            </div>

            <div className={styles['add-card']}>
                <h2 className={styles['add-card-title']}>
                    <FontAwesomeIcon icon={faBuilding} />
                    Neue Branche anlegen
                </h2>
                <div className={styles['add-grid']}>
                    <div className={styles['add-field']}>
                        <label className={styles['field-label']} htmlFor="new-branch-name">
                            <FontAwesomeIcon icon={faTag} />
                            Name
                        </label>
                        <input
                            id="new-branch-name"
                            type="text"
                            className={styles['text-input']}
                            placeholder="z. B. Gastronomie"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    void handleCreate();
                                }
                            }}
                        />
                        <p
                            className={cx(styles['field-hint'], styles['field-hint-empty'])}
                            aria-hidden="true"
                        >
                            {'\u00A0'}
                        </p>
                    </div>

                    <div className={styles['add-field']}>
                        <label className={styles['field-label']} htmlFor="new-category">
                            <FontAwesomeIcon icon={faFolderTree} />
                            Kategorie
                        </label>
                        {categorySelect(newCategoryId, setNewCategoryId, {
                            id: 'new-category',
                            showHint: true,
                        })}
                    </div>

                    <div className={styles['add-field']}>
                        <span className={styles['field-label']}>
                            <FontAwesomeIcon icon={faImage} />
                            Bild
                        </span>
                        <label className={styles['upload-btn']}>
                            <FontAwesomeIcon icon={faImage} />
                            <span>{newPicture ? newPicture.name : 'Datei wählen'}</span>
                            <input
                                ref={newPictureInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className={styles['file-input']}
                                onChange={(e) => setNewPicture(e.target.files?.[0] ?? null)}
                            />
                        </label>
                        <p
                            className={cx(styles['field-hint'], styles['field-hint-empty'])}
                            aria-hidden="true"
                        >
                            {'\u00A0'}
                        </p>
                    </div>

                    <button
                        type="button"
                        className={styles['primary-button']}
                        disabled={creating}
                        onClick={() => void handleCreate()}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {creating ? 'Wird angelegt …' : 'Hinzufügen'}
                    </button>
                </div>
            </div>

            {types.length === 0 ? (
                <div className={styles['empty-state']}>
                    <div className={styles['empty-state-icon']}>
                        <FontAwesomeIcon icon={faBuilding} />
                    </div>
                    <h3>Keine Branchen</h3>
                    <p>Legen Sie oben die erste Branche an.</p>
                </div>
            ) : (
                <div className={styles['table-wrap']}>
                    <div className={styles['table-header']}>
                        <span>Bild</span>
                        <span>Name</span>
                        <span>Kategorie</span>
                        <span>Aktionen</span>
                    </div>

                    {types.map((type) => {
                        const draft = drafts[type.id] ?? {
                            name: type.name,
                            kKategorie: type.kKategorie ?? null,
                        };
                        const isBusy = savingId === type.id;
                        const hasPicture = Boolean(type.picture);
                        const hasCategory = isCategorySelected(draft.kKategorie);

                        return (
                            <div
                                key={type.id}
                                className={cx(
                                    styles['table-row'],
                                    isBusy && styles['table-row-busy']
                                )}
                            >
                                <div className={styles['cell-image']}>
                                    {hasPicture ? (
                                        <img
                                            src={`${pictureBaseUrl}${type.picture}`}
                                            alt={type.name}
                                            className={styles['thumb']}
                                        />
                                    ) : (
                                        <div className={styles['thumb-placeholder']}>
                                            <FontAwesomeIcon icon={faImage} />
                                        </div>
                                    )}
                                </div>

                                <div className={styles['cell-name']}>
                                    <div className={styles['table-field']}>
                                        <input
                                            type="text"
                                            className={styles['text-input']}
                                            placeholder="Name"
                                            value={draft.name}
                                            onChange={(e) =>
                                                updateDraft(type.id, { name: e.target.value })
                                            }
                                        />
                                        <p
                                            className={cx(
                                                styles['field-hint'],
                                                styles['field-hint-empty']
                                            )}
                                            aria-hidden="true"
                                        >
                                            {'\u00A0'}
                                        </p>
                                    </div>
                                </div>

                                <div className={styles['cell-category']}>
                                    <div className={styles['table-field']}>
                                        {categorySelect(draft.kKategorie, (value) =>
                                            updateDraft(type.id, { kKategorie: value }), {
                                            showHint: true,
                                        })}
                                    </div>
                                </div>

                                <div className={styles['cell-actions']}>
                                    <div className={styles['action-toolbar']}>
                                        <button
                                            type="button"
                                            className={cx(
                                                styles['action-btn'],
                                                styles['action-btn-save']
                                            )}
                                            title={
                                                hasCategory
                                                    ? 'Speichern'
                                                    : CATEGORY_REQUIRED_MESSAGE
                                            }
                                            disabled={isBusy || !hasCategory}
                                            onClick={() => void handleSave(type.id)}
                                        >
                                            <FontAwesomeIcon icon={faSave} fixedWidth />
                                            Speichern
                                        </button>
                                        <div className={styles['action-upload-wrap']}>
                                            <button
                                                type="button"
                                                className={cx(
                                                    styles['action-btn'],
                                                    styles['action-btn-upload']
                                                )}
                                                title="Bild hochladen"
                                                disabled={isBusy}
                                                onClick={(e) => {
                                                    const input = e.currentTarget
                                                        .nextElementSibling as HTMLInputElement | null;
                                                    input?.click();
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faImage} fixedWidth />
                                            </button>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/gif"
                                                className={styles['file-input']}
                                                disabled={isBusy}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        void handlePictureUpload(type.id, file);
                                                    }
                                                    e.target.value = '';
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className={cx(
                                                styles['action-btn'],
                                                styles['action-btn-remove'],
                                                !hasPicture && styles['action-btn-remove-empty']
                                            )}
                                            title={
                                                hasPicture
                                                    ? 'Bild entfernen'
                                                    : 'Kein Bild vorhanden'
                                            }
                                            disabled={!hasPicture || isBusy}
                                            onClick={() => openDeletePictureModal(type)}
                                        >
                                            <FontAwesomeIcon icon={faXmark} fixedWidth />
                                        </button>
                                        <button
                                            type="button"
                                            className={cx(
                                                styles['action-btn'],
                                                styles['action-btn-danger']
                                            )}
                                            title="Branche löschen"
                                            disabled={isBusy}
                                            onClick={() => openDeleteBranchModal(type)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} fixedWidth />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TypesOverview;
